import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereePayable } from "types/api-types/payroll/everee-payables/everee-payables.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";

export type PayableStatus =
  | "PROCESSING"
  | "PENDING_APPROVAL"
  | "PENDING_PAYMENT"
  | "PAID"
  | "ERROR"
  | "";
export type PaymentType =
  | "REGULAR_HOURLY"
  | "OVERTIME_HOURLY"
  | "DOUBLE_TIME_HOURLY"
  | "REGULAR_SALARY"
  | "REIMBURSEMENT"
  | "CONTRACTOR"
  | "TIPS"
  | "PER_DIEM"
  | "COMMISSION"
  | "BONUS"
  | "HOLIDAY"
  | "VACATION"
  | "SICK"
  | "ADVANCE"
  | "SEPARATION"
  | "MILEAGE"
  | "EARNINGS_ON_DEMAND"
  | "";

/**
 * EvereePayrollOperationsStore - Manages payables, early pay, and realtime payroll
 */
class EvereePayrollOperationsStore {
  rootStore: RootStore;

  // Observable Properties
  isLoading = false;
  error: string | null = null;
  statusFilter: PayableStatus = "";
  paymentTypeFilter: PaymentType = "";
  workerIdFilter = "";
  selectedPayableId: string | null = null;
  selectedPayableIds: string[] = [];

  // DataGrid state - uses DataGridColumnHeader format (key, title, width)
  currentHeaders: Array<{ key: string; title: string; width: number }> = [
    { key: "payableId", title: "Payable ID", width: 120 },
    { key: "workerName", title: "Worker", width: 180 },
    { key: "amount", title: "Amount", width: 100 },
    { key: "paymentType", title: "Type", width: 120 },
    { key: "status", title: "Status", width: 100 },
    { key: "description", title: "Description", width: 200 },
    { key: "createdAt", title: "Created", width: 140 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereePayablesStore(): ServiceStore {
    return this.rootStore.evereePayablesStore as ServiceStore;
  }

  get evereeEarlyPayStore(): ServiceStore {
    return this.rootStore.evereeEarlyPayStore as ServiceStore;
  }

  get evereeRealtimePayrollStore(): ServiceStore {
    return this.rootStore.evereeRealtimePayrollStore as ServiceStore;
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  // Computed Values
  get payables(): evereePayable[] {
    return (this.evereePayablesStore?.entities ||
      []) as unknown as evereePayable[];
  }

  get workers(): evereeWorkerData[] {
    return (this.evereeWorkerDataStore?.entities ||
      []) as unknown as evereeWorkerData[];
  }

  get workerMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.workers.forEach((w) => {
      map.set(w.workerId, `${w.firstName} ${w.lastName}`);
    });
    return map;
  }

  get filteredPayables(): evereePayable[] {
    let result = this.payables;

    if (this.statusFilter) {
      result = result.filter((p) => p.paymentStatus === this.statusFilter);
    }

    if (this.paymentTypeFilter) {
      result = result.filter((p) => p.earningType === this.paymentTypeFilter);
    }

    if (this.workerIdFilter) {
      result = result.filter((p) => p.externalWorkerId === this.workerIdFilter);
    }

    return result;
  }

  get dataGridData(): Record<string, unknown>[] {
    return this.filteredPayables.map((p) => ({
      id: p.externalId,
      payableId: p.externalId,
      workerId: p.externalWorkerId,
      workerName: this.workerMap.get(p.externalWorkerId) || p.externalWorkerId,
      amount: p.earningAmount?.amount
        ? `$${parseFloat(p.earningAmount.amount).toFixed(2)} ${
            p.earningAmount.currency
          }`
        : "",
      paymentType: p.earningType,
      status: p.paymentStatus,
      description: p.label || "",
      createdAt: p.earningTimestamp
        ? new Date(p.earningTimestamp * 1000).toLocaleDateString()
        : "",
    }));
  }

  get totalCount(): number {
    return this.payables.length;
  }

  get filteredCount(): number {
    return this.filteredPayables.length;
  }

  get pendingPayables(): evereePayable[] {
    return this.payables.filter(
      (p) =>
        p.paymentStatus === "PENDING_APPROVAL" ||
        p.paymentStatus === "PENDING_PAYMENT"
    );
  }

  get totalPendingAmount(): number {
    return this.pendingPayables.reduce((sum, p) => {
      const amount = p.earningAmount?.amount
        ? parseFloat(p.earningAmount.amount)
        : 0;
      return sum + amount;
    }, 0);
  }

  // Actions
  setStatusFilter = (status: PayableStatus) => {
    this.statusFilter = status;
  };

  setPaymentTypeFilter = (type: PaymentType) => {
    this.paymentTypeFilter = type;
  };

  setWorkerIdFilter = (workerId: string) => {
    this.workerIdFilter = workerId;
  };

  setSelectedPayableId = (payableId: string | null) => {
    this.selectedPayableId = payableId;
  };

  togglePayableSelection = (payableId: string) => {
    const index = this.selectedPayableIds.indexOf(payableId);
    if (index === -1) {
      this.selectedPayableIds.push(payableId);
    } else {
      this.selectedPayableIds.splice(index, 1);
    }
  };

  clearSelection = () => {
    this.selectedPayableIds = [];
  };

  clearFilters = () => {
    this.statusFilter = "";
    this.paymentTypeFilter = "";
    this.workerIdFilter = "";
  };

  // Async Actions
  loadPayables = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // Build query with company filter if provided
      const payablesQuery: Record<string, unknown> = { $limit: 1000 };
      const workersQuery: Record<string, unknown> = { page: 0, size: 100 };

      if (companyId) {
        payablesQuery.companyId = companyId;
        workersQuery.companyId = companyId;
      }

      await Promise.all([
        this.evereePayablesStore.find({ query: payablesQuery }),
        // Use everee-worker-data for listing workers (everee-workers only supports create/remove)
        this.evereeWorkerDataStore.find({ query: workersQuery }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load payables";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createPayable = async (data: {
    workerId: string;
    amount: number;
    paymentType: string;
    description?: string;
  }): Promise<boolean> => {
    try {
      await this.evereePayablesStore.create({
        ...data,
        currency: "USD",
      });
      await this.loadPayables();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create payable";
      });
      return false;
    }
  };

  triggerPayout = async (
    payableIds: string[],
    includeWorkersOnRegularPayCycle = false
  ): Promise<boolean> => {
    try {
      // Trigger payout action
      await this.evereePayablesStore.create({
        action: "trigger-payout",
        payableIds,
        includeWorkersOnRegularPayCycle,
      });
      await this.loadPayables();
      this.clearSelection();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to trigger payout";
      });
      return false;
    }
  };

  getEarlyPayStatus = async (workerId: string) => {
    try {
      return await this.evereeEarlyPayStore.get(workerId);
    } catch (error) {
      console.error("Failed to get early pay status:", error);
      return null;
    }
  };

  requestEarlyPay = async (
    workerId: string,
    amount: number
  ): Promise<boolean> => {
    try {
      await this.evereeEarlyPayStore.create({
        workerId,
        requestedAmount: amount,
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to request early pay";
      });
      return false;
    }
  };

  toggleRealtimePayroll = async (
    workerId: string,
    enabled: boolean
  ): Promise<boolean> => {
    try {
      await this.evereeRealtimePayrollStore.patch(workerId, { enabled });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update realtime payroll setting";
      });
      return false;
    }
  };

  reset = () => {
    this.isLoading = false;
    this.error = null;
    this.statusFilter = "";
    this.paymentTypeFilter = "";
    this.workerIdFilter = "";
    this.selectedPayableId = null;
    this.selectedPayableIds = [];
  };
}

export default EvereePayrollOperationsStore;
